/**
 * TwaReport - TWA-Auswertung (Taktwaschanlage)
 * 
 * Zeigt separate Bereiche für P50 und P25:
 * - Jeder Bereich hat eigene KPIs, Charts und Schichtdaten-Tabelle
 * - Keine Tabs, beide Bereiche auf einer Seite
 */

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Calendar,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { TwaMachineSection } from '../components/TwaMachineSection';
import { ExportButtonsPlaceholder } from '../components/ExportButtonsPlaceholder';
import {
  getTodayBerlin,
  getQuickPickerRange,
  type QuickPicker,
} from '@wash-ops/shared';
import { formatBerlinDateDE } from '../utils/berlinDate';

type Preset = QuickPicker | 'custom';

function formatDateDE(dateStr: string): string {
  const [y, m, d] = dateStr.split('-');
  return `${d}.${m}.${y}`;
}

function getRangeLabel(preset: Preset, startDate: string, endDate: string): string {
  const today = getTodayBerlin();
  const startDE = formatDateDE(startDate);
  const endDE = formatDateDE(endDate);
  
  switch (preset) {
    case 'today':
      return `${startDE} (bis jetzt)`;
    case 'yesterday':
    case 'dayBefore':
      return startDE;
    case 'thisWeek':
    case 'thisMonth':
      return `${startDE} – ${endDE} (bis heute)`;
    case 'lastWeek':
    case 'lastMonth':
      return `${startDE} – ${endDE}`;
    case 'custom':
      if (startDate === endDate) {
        return startDE;
      }
      if (endDate >= today) {
        return `${startDE} – ${endDE} (bis heute)`;
      }
      return `${startDE} – ${endDE}`;
    default:
      return `${startDE} – ${endDE}`;
  }
}

const PRESET_LABELS: Record<Preset, string> = {
  today: 'Heute',
  yesterday: 'Gestern',
  dayBefore: 'Vorgestern',
  thisWeek: 'Diese Woche',
  lastWeek: 'Letzte Woche',
  thisMonth: 'Dieser Monat',
  lastMonth: 'Letzter Monat',
  custom: 'Zeitraum wählen',
};

export default function TwaReportPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialPreset = (searchParams.get('preset') as Preset) || 'today';
  const [preset, setPreset] = useState<Preset>(
    ['today', 'yesterday', 'dayBefore', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth', 'custom'].includes(initialPreset)
      ? initialPreset
      : 'today'
  );

  useEffect(() => {
    if (preset !== 'custom') {
      setSearchParams({ preset }, { replace: true });
    }
  }, [preset, setSearchParams]);

  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const range = useMemo(() => {
    if (preset === 'custom') {
      if (customStart && customEnd) {
        return { start: customStart, end: customEnd };
      }
      const today = getTodayBerlin();
      return { start: today, end: today };
    }
    const r = getQuickPickerRange(preset);
    return { start: r.startDate, end: r.endDate };
  }, [preset, customStart, customEnd]);

  const fromDate = range.start;
  const toDate = range.end;

  const vizMode = useMemo(() => {
    if (preset === 'today' || preset === 'yesterday' || preset === 'dayBefore') {
      return 'single-day' as const;
    }
    if (preset === 'thisWeek' || preset === 'lastWeek') {
      return 'week' as const;
    }
    return 'month' as const;
  }, [preset]);

  const partialRangeInfo = useMemo(() => {
    const today = getTodayBerlin();
    
    if (preset === 'today') {
      return { type: 'info' as const, message: 'Stand: heute bis jetzt (laufender Tag)' };
    }
    
    if (preset === 'thisWeek' || preset === 'thisMonth') {
      return { 
        type: 'warn' as const, 
        message: `Zeitraum nicht abgeschlossen - Werte bis ${formatBerlinDateDE(today)} sind vorläufig.`
      };
    }
    
    if (preset === 'custom' && customEnd && customEnd >= today) {
      return { 
        type: 'warn' as const, 
        message: `Zeitraum nicht abgeschlossen - Werte bis ${formatBerlinDateDE(today)} sind vorläufig.`
      };
    }
    
    return { type: 'none' as const, message: '' };
  }, [preset, customEnd]);

  const isPartialRange = partialRangeInfo.type !== 'none';

  const applyPreset = (p: Preset) => {
    if (p !== 'custom') {
      setCustomStart('');
      setCustomEnd('');
    }
    setPreset(p);
  };

  // Shift-Types für Dropdown
  const [shiftTypes, setShiftTypes] = useState<Array<{ code: string; label: string }>>([
    { code: 'EARLY', label: 'Frühschicht' },
    { code: 'LATE', label: 'Spätschicht' },
    { code: 'SATURDAY', label: 'Samstag' },
  ]);

  useEffect(() => {
    fetch('/api/shifts/types', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setShiftTypes(data.map((t: { code: string; label: string }) => ({
            code: t.code,
            label: t.label,
          })));
        }
      })
      .catch(() => {});
  }, []);

  // Refresh-Trigger für beide Sections nach Korrektur
  const [refreshKey, setRefreshKey] = useState(0);
  const handleCorrectionSaved = () => {
    setRefreshKey(k => k + 1);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">TWA-Auswertung</h1>
            <p className="text-sm text-slate-500">Taktwaschanlage - P50 und P25 getrennt</p>
          </div>
          <ExportButtonsPlaceholder />
        </div>

        {/* Zeitraum-Auswahl */}
        <div className="bg-white rounded-xl p-4 shadow-sm border space-y-4">
          <div className="flex flex-wrap gap-2">
            {(['today', 'yesterday', 'dayBefore', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth'] as Preset[]).map((p) => (
              <button
                key={p}
                onClick={() => applyPreset(p)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  preset === p
                    ? 'bg-blue-600 text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {PRESET_LABELS[p]}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-4 items-center">
            <Calendar className="w-5 h-5 text-gray-400" />
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customStart || fromDate}
                onChange={(e) => {
                  setCustomStart(e.target.value);
                  setPreset('custom');
                }}
                className="input"
              />
              <span className="text-gray-400">bis</span>
              <input
                type="date"
                value={customEnd || toDate}
                onChange={(e) => {
                  setCustomEnd(e.target.value);
                  setPreset('custom');
                }}
                className="input"
              />
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 px-4 py-2.5 rounded-md border border-slate-200">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span className="font-medium">{getRangeLabel(preset, fromDate, toDate)}</span>
            </div>
          </div>
        </div>

        {/* Hinweise */}
        {partialRangeInfo.type === 'warn' && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Zeitraum nicht abgeschlossen</p>
              <p className="text-sm text-amber-700">{partialRangeInfo.message}</p>
            </div>
          </div>
        )}
        {partialRangeInfo.type === 'info' && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-700">{partialRangeInfo.message}</p>
          </div>
        )}

        {/* P50 Bereich */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <TwaMachineSection
            key={`p50-${refreshKey}`}
            machineCode="TWA_P50"
            title="50kg Taktwaschanlage (P50)"
            fromDate={fromDate}
            toDate={toDate}
            vizMode={vizMode}
            isPartialRange={isPartialRange}
            shiftTypes={shiftTypes}
            onCorrectionSaved={handleCorrectionSaved}
          />
        </div>

        {/* P25 Bereich */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <TwaMachineSection
            key={`p25-${refreshKey}`}
            machineCode="TWA_P25"
            title="25kg Taktwaschanlage (P25)"
            fromDate={fromDate}
            toDate={toDate}
            vizMode={vizMode}
            isPartialRange={isPartialRange}
            shiftTypes={shiftTypes}
            onCorrectionSaved={handleCorrectionSaved}
          />
        </div>
      </div>
    </AdminLayout>
  );
}
